import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Users, Search, DollarSign, Target, Gauge, Percent } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AppLayout } from "@/components/layout/AppLayout";
import { PeriodSelector } from "@/components/dashboard/PeriodSelector";
import { SalespeopleTable } from "@/components/dashboard/SalespeopleTable";
import { KpiCard } from "@/components/dashboard/KpiCard";
import { Input } from "@/components/ui/input";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/StateViews";
import { fmtBRL, fmtPct, fmtNum, PERIODS } from "@/lib/format";

const fetchSalespeople = ({ period }) =>
  api.get("/salespeople", { params: { period } }).then((r) => r.data);

export default function Salespeople() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("month");
  const [search, setSearch] = useState("");

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["salespeople", period],
    queryFn: () => fetchSalespeople({ period }),
    keepPreviousData: true,
  });

  const rows = useMemo(() => data?.salespeople || [], [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(q));
  }, [rows, search]);

  const totals = useMemo(() => {
    const receita = rows.reduce((s, r) => s + r.receita, 0);
    const meta = rows.reduce((s, r) => s + r.meta, 0);
    const vendas = rows.reduce((s, r) => s + r.num_vendas, 0);
    const atingimento = meta > 0 ? (receita / meta) * 100 : 0;
    return { receita, meta, vendas, atingimento, count: rows.length };
  }, [rows]);

  const periodLabel = PERIODS.find((p) => p.value === period)?.label;

  return (
    <AppLayout>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            <Users className="h-7 w-7 text-primary" />
            Vendedores
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Equipe de vendas · {periodLabel}
          </p>
        </div>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {isLoading && <LoadingState label="Carregando vendedores..." />}
      {isError && (
        <ErrorState
          message={formatApiErrorDetail(error?.response?.data?.detail)}
          onRetry={refetch}
        />
      )}

      {data && !isError && (
        <div className={isFetching ? "opacity-70 transition-opacity" : "transition-opacity"}>
          {/* Indicadores agregados da equipe */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              testId="team-kpi-receita"
              title="Receita da equipe"
              value={fmtBRL(totals.receita)}
              icon={DollarSign}
              footer={`${totals.count} vendedores`}
            />
            <KpiCard
              testId="team-kpi-meta"
              title="Meta da equipe"
              value={fmtBRL(totals.meta)}
              icon={Target}
            />
            <KpiCard
              testId="team-kpi-atingimento"
              title="Atingimento"
              value={fmtPct(totals.atingimento)}
              icon={Percent}
            />
            <KpiCard
              testId="team-kpi-vendas"
              title="Total de vendas"
              value={fmtNum(totals.vendas)}
              icon={Gauge}
            />
          </div>

          {/* Busca */}
          <div className="mt-6 flex items-center gap-3">
            <div className="relative w-full max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="salespeople-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar vendedor por nome..."
                className="h-10 pl-10"
              />
            </div>
            <span className="text-sm text-muted-foreground">
              {filtered.length} de {rows.length}
            </span>
          </div>

          {/* Lista */}
          <div className="mt-4">
            {filtered.length === 0 ? (
              <EmptyState
                message={
                  search
                    ? `Nenhum vendedor encontrado para "${search}".`
                    : "Sem vendedores no período."
                }
              />
            ) : (
              <SalespeopleTable rows={filtered} />
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
